import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BeyondPaginationModel } from '@base/model/common/beyond-pagination.model';
import { PaginationResponseModel } from '@base/model/common/pagination-response.model';
import { OrganizationLogoModel } from '@base/model/organization/organization-logo.model';
import { PageableRestService } from '@base/modules/rest/common/pageable-rest.service';
import { OrganizationModel } from '@base/modules/rest/organization/model/organization.model';
import { OrgDiagramResponseModel } from '@base/modules/rest/organization/response/org-diagram-response.model';
import { AppConfiguration } from 'app/app-configuration';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrganizationRestService implements PageableRestService<OrganizationModel> {

  private readonly controllerUrl: string = this.appConfiguration.serverApiUrl + '/secured/organizations';

  constructor(private httpClient: HttpClient,
              private appConfiguration: AppConfiguration) {
  }

  getAll(): Observable<OrganizationModel[]> {
    return this.httpClient.get<OrganizationModel[]>(`${this.controllerUrl}/all`);
  }

  findAll(payload: {
    pagination: BeyondPaginationModel,
    includeNonMcm?: boolean
  }): Observable<PaginationResponseModel<OrganizationModel>> {
    const queryParams: Params = {
      page: payload.pagination.page,
      size: payload.pagination.size,
      sort: payload.pagination.sort ?? [],
      filter: payload.pagination.filter || '',
      includeNonMcm: payload.includeNonMcm,
    };
    return this.httpClient.get<PaginationResponseModel<OrganizationModel>>(this.controllerUrl, { params: queryParams });
  }

  findAllForOverview(payload: { pagination: BeyondPaginationModel; includeInactive?: boolean }): Observable<PaginationResponseModel<OrganizationModel>> {
    const queryParams: Params = {
      page: payload.pagination.page,
      size: payload.pagination.size,
      sort: payload.pagination.sort,
      filter: payload.pagination.filter || '',
      includeInactive: payload.includeInactive ? payload.includeInactive : false,
    };
    return this.httpClient.get<PaginationResponseModel<OrganizationModel>>(this.controllerUrl, { params: queryParams });
  }

  findOne(payload: { id: number }): Observable<OrganizationModel> {
    return this.httpClient.get<OrganizationModel>(`${this.controllerUrl}/${payload.id}`);
  }

  create(payload: { organization: OrganizationModel }): Observable<OrganizationModel> {
    return this.httpClient.post<OrganizationModel>(this.controllerUrl, payload.organization);
  }

  update(payload: { id: number; organization: OrganizationModel }): Observable<OrganizationModel> {
    return this.httpClient.put<OrganizationModel>(`${this.controllerUrl}/${payload.id}`, payload.organization);
  }

  uploadLogo(payload: { organizationId: number; organizationLogo: OrganizationLogoModel }): Observable<OrganizationModel> {
    return this.httpClient.post<OrganizationModel>(`${this.controllerUrl}/logo/${payload.organizationId}`, payload.organizationLogo);
  }

  uploadEmailLogo(payload: { organizationId: number; organizationLogo: OrganizationLogoModel }): Observable<OrganizationModel> {
    return this.httpClient.post<OrganizationModel>(`${this.controllerUrl}/logo/${payload.organizationId}/email`, payload.organizationLogo);
  }

  deleteEmailLogo(payload: { organizationId: number }): Observable<OrganizationModel> {
    return this.httpClient.put<OrganizationModel>(`${this.controllerUrl}/logo/${payload.organizationId}/email`, null);
  }

  getOrganizationDiagramData(): Observable<OrgDiagramResponseModel> {
    return this.httpClient.get<OrgDiagramResponseModel>(`${this.controllerUrl}/diagram`);
  }

  isCodeAvailable(payload: { code: string }): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this.controllerUrl}/available-code`, `${payload.code}`);
  }
}
